/**
 * Navigation model of the application shell.
 *
 * The list lives in a module rather than inside the sidebar for two reasons: the same items drive the mobile drawer
 * and the future command palette, and the role gate is expressed once here instead of being repeated as a condition
 * in the markup.
 */

import {
  FileCheck2,
  Folders,
  KeyRound,
  LayoutDashboard,
  Library,
  Percent,
  PlusCircle,
  Settings,
  ShieldCheck,
  Users,
  WandSparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { PROMPT_FORGE_ROLES } from '@/types/api';
import type { UserProfile } from '@/types/api';

/** Role name that unlocks the administration area. */
const ADMIN_ROLE: string = PROMPT_FORGE_ROLES[0];

/** One entry of the shell navigation. */
export interface NavItem {
  /** Stable identifier, also used as a React key. */
  key: string;
  /** Translation key of the label. */
  labelKey: string;
  /** Route the entry navigates to. */
  to: string;
  /** Icon shown next to the label. */
  icon: LucideIcon;
  /** When true, the entry is only rendered for administrators. */
  adminOnly?: boolean;
}

/**
 * Entries grouped under the "workspace" heading.
 *
 * There is exactly **one** entry that starts a prompt, and since TASK-0021 it points at the framework-driven wizard: the
 * questionnaire it replaced is reachable only through the address of a draft that already exists, so a second entry would
 * offer a way into the product that creates nothing. The label is the one the create entry always had («ساخت پرامپت»), so
 * the retirement is invisible to the reader of the sidebar.
 */
export const workspaceNav: readonly NavItem[] = [
  { key: 'dashboard', labelKey: 'nav.dashboard', to: '/dashboard', icon: LayoutDashboard },
  { key: 'wizard', labelKey: 'nav.wizard', to: '/wizard/framework', icon: PlusCircle },
  { key: 'generate', labelKey: 'nav.generate', to: '/generate', icon: WandSparkles },
  { key: 'prompts', labelKey: 'nav.prompts', to: '/prompts', icon: Folders },
  { key: 'templates', labelKey: 'nav.templates', to: '/templates', icon: Library },
] as const;

/** Entries grouped under the "account" heading. */
export const accountNav: readonly NavItem[] = [
  { key: 'settings', labelKey: 'nav.settings', to: '/settings', icon: Settings },
  { key: 'admin', labelKey: 'nav.admin', to: '/admin', icon: ShieldCheck, adminOnly: true },
  /**
   * The moderator's queue (TASK-0050), beside the administration entry rather than inside it: approving a submission is
   * the one administrative task a moderator does several times a session, and burying the queue one navigation deeper
   * would cost a click for every review.
   */
  {
    key: 'publicationReview',
    labelKey: 'nav.publicationReview',
    to: '/admin/publication-review',
    icon: FileCheck2,
    adminOnly: true,
  },
  /**
   * The account directory (TASK-0061), beside the administration entry: finding an account is the administrative task that
   * starts a support conversation, and it is reached several times a day rather than once a visit.
   */
  {
    key: 'adminUsers',
    labelKey: 'nav.adminUsers',
    to: '/admin/users',
    icon: Users,
    adminOnly: true,
  },
  /**
   * The registration policy (TASK-0062), beside the account directory: it is the *system-wide* counterpart of the per-account
   * verification card. The card answers "has this person proved their address?", and this screen answers "what must the next
   * person prove, and how?". The card is not a substitute for it — it can verify one account, not change what every new
   * registration is asked for — so the screen needs its own entry rather than living one level inside a user's detail page.
   * It also carries a global read (`GET /auth/policy`, for the provider availability rows the API publishes there), which is
   * why the page outlives any single account.
   */
  {
    key: 'adminAuthPolicy',
    labelKey: 'nav.adminAuthPolicy',
    to: '/admin/auth-policy',
    icon: KeyRound,
    adminOnly: true,
  },
  /**
   * The discount codes (TASK-0073), beside the other administrative screens: issuing a code is a small, dated action that
   * starts a campaign, so it belongs where the operator already is rather than behind the administration landing page.
   */
  {
    key: 'adminDiscounts',
    labelKey: 'nav.adminDiscounts',
    to: '/admin/discounts',
    icon: Percent,
    adminOnly: true,
  },
] as const;

/**
 * Determines whether an account holds the administrator role.
 *
 * `roles` is matched with an exact comparison because the API ships role names from `PromptForgeRoles`; a
 * case-insensitive match here would hide a genuine contract violation instead of surfacing it.
 *
 * @param user Profile to inspect, or `null` when signed out.
 * @returns True when the account is an administrator.
 */
export function isAdministrator(user: UserProfile | null): boolean {
  return user?.roles.includes(ADMIN_ROLE) ?? false;
}

/**
 * Filters a navigation list for an account.
 *
 * @param items Entries to filter.
 * @param user Signed-in profile, or `null`.
 * @returns The entries the account may see.
 */
export function visibleNavItems(items: readonly NavItem[], user: UserProfile | null): NavItem[] {
  const admin = isAdministrator(user);

  return items.filter((item) => !item.adminOnly || admin);
}
